// src/models/ProvisioningQR.js
// ─────────────────────────────────────────────────────────────────────────────

class ProvisioningQR {
  constructor(data = {}) {
    this.id          = data.id || data._id || null;
    this.name        = data.name        || '';
    this.description = data.description || '';

    // ── Type ──────────────────────────────────────────────────────────────
    // 'new_device_single'  → Fresh enrollment (Hello screen QR)
    // 'running_single'     → Already-setup device re-enrollment
    // 'group'       → Bulk group enrollment
    this.type = data.type || 'new_device_single';

    // ── Company / App binding ─────────────────────────────────────────────
    this.companyId   = data.companyId   || null;
    this.companyName = data.companyName || '';
    this.appName     = data.appName     || 'Tracklio';
    this.appPackage  = data.appPackage  || 'com.trackliohub.tracklio';

    // ── Group (only for type=group) ───────────────────────────────────────
    this.groupId   = data.groupId   || null;
    this.groupName = data.groupName || '';

    // ── Limits ────────────────────────────────────────────────────────────
    this.deviceLimit    = data.deviceLimit    !== undefined ? Number(data.deviceLimit)    : null; // null = unlimited
    this.enrolledCount  = data.enrolledCount  !== undefined ? Number(data.enrolledCount)  : 0;

    // ── Expiry ────────────────────────────────────────────────────────────
    this.expiresAt    = data.expiresAt    ? new Date(data.expiresAt)    : null; // null = never
    this.isActive     = data.isActive     !== undefined ? Boolean(data.isActive) : true;

    // ── QR payload ────────────────────────────────────────────────────────
    this.qrToken     = data.qrToken     || '';
    this.qrImageUrl  = data.qrImageUrl  || null;   // base64 or URL
    this.qrData      = data.qrData      || null;   // raw JSON string inside QR

    // ── Audit ─────────────────────────────────────────────────────────────
    this.createdAt = data.createdAt ? new Date(data.createdAt) : new Date();
    this.updatedAt = data.updatedAt ? new Date(data.updatedAt) : new Date();
    this.createdBy = data.createdBy || null;
  }

  // ── Computed status ────────────────────────────────────────────────────────
  get status() {
    if (!this.isActive) return 'Inactive';
    if (this.expiresAt && new Date() > this.expiresAt) return 'Expired';
    if (this.deviceLimit !== null && this.enrolledCount >= this.deviceLimit) return 'Limit Reached';
    return 'Active';
  }

  get isExpired() {
    return this.expiresAt ? new Date() > this.expiresAt : false;
  }

  get isLimitReached() {
    return this.deviceLimit !== null ? this.enrolledCount >= this.deviceLimit : false;
  }

  get isUsable() {
    return this.isActive && !this.isExpired && !this.isLimitReached;
  }

  get usagePercent() {
    if (this.deviceLimit === null) return null;
    return Math.min(100, Math.round((this.enrolledCount / this.deviceLimit) * 100));
  }

  // ── Type label ────────────────────────────────────────────────────────────
  get typeLabel() {
    return { new_device_single: 'New Device', running_single: 'Running Device', new_device_group: 'Group Enrollment' }[this.type] || this.type;
  }

  // ── Validation ────────────────────────────────────────────────────────────
  validate() {
    const errors = [];
    if (!this.name?.trim())      errors.push('QR name is required');
    if (!this.companyId)         errors.push('Company is required');
    if (!this.appPackage?.trim()) errors.push('App package name is required');
    if (!['new_device_single','running_single','new_device_group'].includes(this.type)) errors.push('Invalid QR type');
    if (this.type === 'new_device_group' && !this.groupId) errors.push('Group is required for group QR');
    if (this.deviceLimit !== null && (isNaN(this.deviceLimit) || this.deviceLimit < 1))
      errors.push('Device limit must be a positive number');
    return { isValid: errors.length === 0, errors };
  }

  toJSON() {
    return {
      id:           this.id,
      name:         this.name,
      description:  this.description,
      type:         this.type,
      companyId:    this.companyId,
      appName:      this.appName,
      appPackage:   this.appPackage,
      groupId:      this.groupId,
      groupName:    this.groupName,
      deviceLimit:  this.deviceLimit,
      expiresAt:    this.expiresAt ? this.expiresAt.toISOString() : null,
      isActive:     this.isActive,
    };
  }

  static fromJSON(data) {
    if (!data) return null;
    return new ProvisioningQR(data);
  }
}

export default ProvisioningQR;